import type { FC, ReactElement } from 'react';

import Enums from '@core/types/api/Enums';

import { useSlabQuery } from '../../hooks/useSlabQuery';
import { type AsyncJobFailedItem, AsyncJobFailedItemsModal } from './AsyncJobFailedItemsModal';
import { AsyncJobFailedItemsPreview } from './AsyncJobFailedItemsPreview';
import type {
  AsyncJobFailureDetailsProps,
  AsyncJobFailurePreviewProps,
} from './AsyncJobStatusConfig';
import { useAsyncJobFailedObjectIds } from './useAsyncJobFailedObjectIds';

const FAILURE_DETAIL_POLL_INTERVAL_MS = 5000;

type FailedItemsResult = {
  failedCount: number;
  items: AsyncJobFailedItem[];
  isLoading: boolean;
  isError: boolean;
};

const useQuotePriceEscalationFailedItems = ({
  jobId,
  summaryFailedCount,
  enabled,
  isProcessing,
}: {
  jobId: string;
  summaryFailedCount: number;
  enabled: boolean;
  isProcessing: boolean;
}): FailedItemsResult => {
  const {
    failedObjectIds: failedEscalationIds,
    failedObjects,
    itemsFailed: detailFailedCount,
    isLoading: isLoadingJob,
    isError: isJobError,
  } = useAsyncJobFailedObjectIds(
    jobId,
    enabled,
    enabled && isProcessing ? FAILURE_DETAIL_POLL_INTERVAL_MS : false,
  );

  const {
    data: escalationSummaries,
    isLoading: isLoadingEscalations,
    isError: isEscalationError,
  } = useSlabQuery(
    'GET quote price escalation summaries',
    {
      queryParams: {
        page: 0,
        perPage: failedEscalationIds.length,
        filterBy: [
          {
            operation: Enums.FilterOperation.In,
            name: 'id',
            values: failedEscalationIds,
          },
        ],
      },
    },
    {
      enabled: enabled && failedEscalationIds.length > 0,
    },
  );

  const reasonByEscalationId = new Map(
    failedObjects
      .filter(({ reason }) => reason !== null && reason !== '')
      .map(({ objectId, reason }) => [objectId, reason ?? '']),
  );
  const escalationById = new Map(
    (escalationSummaries?.items ?? []).map((escalation) => [escalation.id, escalation]),
  );
  const items: AsyncJobFailedItem[] = failedEscalationIds.flatMap((escalationId) => {
    const escalation = escalationById.get(escalationId);
    if (escalation === undefined) {
      return [];
    }

    const reason = reasonByEscalationId.get(escalation.id);

    return [
      {
        id: escalation.id,
        primaryText: `${escalation.quoteNumber} - ${escalation.projectName}`,
        ...(escalation.companyName === null ? {} : { secondaryText: escalation.companyName }),
        ...(reason === undefined ? {} : { reason }),
        destination: `/quotes/${escalation.quoteId}`,
      },
    ];
  });
  const failedCount = Math.max(
    summaryFailedCount,
    detailFailedCount,
    failedEscalationIds.length,
    failedObjects.length,
  );

  return {
    failedCount,
    items,
    isLoading: isLoadingJob || isLoadingEscalations,
    isError: isJobError || isEscalationError,
  };
};

export const QuotePriceEscalationFailedModal: FC<AsyncJobFailureDetailsProps> = ({
  isOpen,
  onClose,
  jobId,
  summaryFailedCount,
  isProcessing,
}): ReactElement => {
  const { failedCount, items, isLoading, isError } = useQuotePriceEscalationFailedItems({
    jobId,
    summaryFailedCount,
    enabled: isOpen,
    isProcessing,
  });

  return (
    <AsyncJobFailedItemsModal
      isOpen={isOpen}
      onClose={onClose}
      title='Failed to apply escalations'
      description={`${failedCount} escalation${failedCount === 1 ? '' : 's'} could not be applied`}
      actionLabel='Solve Issue'
      failedCount={failedCount}
      items={items}
      isLoading={isLoading}
      isError={isError}
    />
  );
};

export const QuotePriceEscalationFailedPreview: FC<AsyncJobFailurePreviewProps> = ({
  jobId,
  summaryFailedCount,
  isProcessing,
  actionLabel,
  onViewFailures,
}): ReactElement => {
  const { failedCount, items, isLoading, isError } = useQuotePriceEscalationFailedItems({
    jobId,
    summaryFailedCount,
    enabled: true,
    isProcessing,
  });

  return (
    <AsyncJobFailedItemsPreview
      actionLabel={actionLabel}
      failedCount={failedCount}
      items={items}
      isLoading={isLoading}
      isError={isError}
      onViewFailures={onViewFailures}
    />
  );
};
